import {
  Caption1,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemCheckbox,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  makeStyles,
  tokens,
  type MenuCheckedValueChangeData,
} from "@fluentui/react-components";
import {
  Bot16Regular,
  ChevronLeft16Regular,
  ChevronRight16Regular,
  ClipboardTextLtr16Regular,
  Dismiss16Regular,
  Document16Regular,
  Filter16Regular,
  GanttChart16Regular,
  History16Regular,
  MoreHorizontal16Regular,
  Person16Regular,
  TaskListSquareLtr16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { FilterLabel, ListToolbar, menuBackLabel } from "@/components/list-toolbar";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { planVisibilityOf, showInPlanList, usePlanVisibilityTick } from "@/features/access/plan-visibility";
import { useWorkspaceRole } from "@/features/access/workspace-access";
import { RecordStatusBadge, SeverityBadge } from "@/features/records/record-ui";
import { RecordDrawer } from "@/features/records/RecordDrawer";
import { ExecutionTaskDrawer } from "@/features/tasks/ExecutionTaskDrawer";
import { TaskStatusChip } from "@/features/tasks/task-ui";
import { formatDate } from "@/lib/date-format";
import { contentShell, listBody, shellGutter } from "@/shell/content-frame";
import { useCatalogStore } from "@/store/catalog-store";
import { STATUS_LABEL } from "@/store/execution-task-ops";
import { useProjectStore } from "@/store/project-store";
import { recordStatusLabel, SEVERITY_LABEL } from "@/store/record-ops";
import type { Project } from "@/engine/types";
import {
  ACTIVITY_KIND_LABEL,
  ACTIVITY_KIND_ORDER,
  ACTIVITY_PAGE_DAYS,
  ALL_ACTIVITY_KINDS,
  activityActors,
  activityScale,
  activityWindowStart,
  collectActivity,
  directionParticle,
  filterActivity,
  groupActivity,
  sameActivityKinds,
  sliceActivity,
  timeOf,
  type ActivityEvent,
  type ActivityKind,
} from "./activity-model";
import { ACTIVITY_MOCK_CASES, activityMockEvents, setActivityMockCase, useActivityMockCase, type ActivityMockCase } from "./activity-mock";
import { openActivity } from "./activity-open";

const DAY_PAGE = 15;

type FilterSection = "kind" | "actor";

const SECTION_LABEL: Record<FilterSection, string> = {
  kind: "종류",
  actor: "사람",
};

function kindIcon(kind: ActivityKind): ReactElement {
  if (kind === "task") return <TaskListSquareLtr16Regular />;
  if (kind === "issue") return <ClipboardTextLtr16Regular />;
  if (kind === "schedule") return <GanttChart16Regular />;
  if (kind === "document") return <Document16Regular />;
  return <History16Regular />;
}

function selectionHint(labels: string[]): string | undefined {
  if (labels.length === 0) return undefined;
  if (labels.length <= 2) return labels.join(", ");
  return `${labels[0]} 외 ${labels.length - 1}`;
}

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  body: {
    ...listBody,
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
  },
  group: {
    display: "flex",
    flexDirection: "column",
  },
  dayHead: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    margin: 0,
    paddingTop: "12px",
    paddingBottom: "4px",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  dayTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  dayMore: {
    display: "flex",
    padding: "4px 4px 8px",
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 4px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    outline: "none",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-1px",
    },
  },
  avatar: {
    flexShrink: 0,
    paddingTop: "1px",
  },
  systemAvatar: {
    width: "24px",
    height: "24px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
    gap: "2px",
  },
  actionLine: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  actorName: {
    color: tokens.colorBrandForeground1,
  },
  actorBtn: {
    display: "inline",
    background: "none",
    border: "none",
    padding: 0,
    margin: 0,
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: tokens.fontWeightRegular,
    lineHeight: "inherit",
    color: tokens.colorBrandForeground1,
    cursor: "pointer",
    textAlign: "left",
    ":hover": {
      color: tokens.colorBrandForeground2,
      textDecorationLine: "underline",
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  inlineChip: {
    display: "inline-flex",
    alignItems: "center",
    verticalAlign: "middle",
    marginLeft: "4px",
    marginRight: "4px",
    padding: "0 6px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1Hover,
    whiteSpace: "nowrap",
  },
  metaLine: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
  },
  time: {
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  planBtn: {
    display: "inline-block",
    maxWidth: "180px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    background: "none",
    border: "none",
    padding: 0,
    margin: 0,
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: tokens.fontWeightRegular,
    lineHeight: "inherit",
    color: "inherit",
    cursor: "pointer",
    textAlign: "left",
    ":hover": {
      textDecorationLine: "underline",
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  kind: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  nowrap: { whiteSpace: "nowrap", flexShrink: 0 },
  hint: {
    display: "block",
    maxWidth: "140px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
  },
  planFilter: { width: "180px" },
  mockBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  more: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `${tokens.spacingVerticalL} 0`,
  },
  empty: {
    flex: 1,
    minHeight: "260px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalM,
  },
});

function VerbText({ event }: { event: ActivityEvent }): ReactNode {
  const styles = useStyles();
  const value = event.value;
  if (value?.type === "taskStatus") {
    const label = STATUS_LABEL[value.status];
    return (<><span> 상태를</span><span className={styles.inlineChip}><TaskStatusChip status={value.status} /></span><span>{`${directionParticle(label)} 변경했습니다`}</span></>);
  }
  if (value?.type === "recordStatus") {
    const label = recordStatusLabel(value.recordKind, value.status);
    return (<><span> 상태를</span><span className={styles.inlineChip}><RecordStatusBadge kind={value.recordKind} status={value.status} /></span><span>{`${directionParticle(label)} 변경했습니다`}</span></>);
  }
  if (value?.type === "severity") {
    const label = SEVERITY_LABEL[value.severity];
    return (<><span> 심각도를</span><span className={styles.inlineChip}><SeverityBadge severity={value.severity} /></span><span>{`${directionParticle(label)} 변경했습니다`}</span></>);
  }
  return <span>{event.verb}</span>;
}

function ActivityRow({
  event,
  showPlan,
  onOpen,
  onFilterActor,
  onFilterPlan,
}: {
  event: ActivityEvent;
  showPlan: boolean;
  onOpen: (event: ActivityEvent) => void;
  onFilterActor: (actor: string) => void;
  onFilterPlan: (planId: string) => void;
}) {
  const styles = useStyles();
  const clock = formatDate(event.at, "clock");
  const actorName = event.actor || "시스템";
  const actor = event.actor ? (
    <button
      type="button"
      className={styles.actorBtn}
      onClick={(e) => {
        e.stopPropagation();
        onFilterActor(event.actor);
      }}
    >
      {actorName}
    </button>
  ) : (
    <span className={styles.actorName}>{actorName}</span>
  );

  return (
    <div
      role="article"
      tabIndex={0}
      className={styles.row}
      onClick={() => onOpen(event)}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen(event);
        }
      }}
      aria-label={`${actorName} ${event.subject}${event.verb}`}
    >
      <div className={styles.avatar}>
        {event.actor ? (
          <PersonAvatar name={event.actor} size={24} tip />
        ) : (
          <div className={styles.systemAvatar} aria-hidden>
            <Bot16Regular />
          </div>
        )}
      </div>
      <div className={styles.content}>
        <p className={styles.actionLine}>{actor}{` ${event.subject}`}<VerbText event={event} /></p>
        <div className={styles.metaLine}>
          <span className={styles.time}>{clock}</span>
          {showPlan ? (
            <Tooltip content={event.planName} relationship="label">
              <button
                type="button"
                className={styles.planBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onFilterPlan(event.planId);
                }}
              >
                {event.planName}
              </button>
            </Tooltip>
          ) : null}
          <span className={styles.kind}>
            {kindIcon(event.kind)}
            <span>{ACTIVITY_KIND_LABEL[event.kind]}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function ActivityFilter({
  kinds,
  actors,
  actorOptions,
  delayedOnly,
  onKinds,
  onActors,
  onDelayed,
  onReset,
}: {
  kinds: ActivityKind[];
  actors: string[];
  actorOptions: string[];
  delayedOnly: boolean;
  onKinds: (kinds: ActivityKind[]) => void;
  onActors: (actors: string[]) => void;
  onDelayed: (delayedOnly: boolean) => void;
  onReset: () => void;
}) {
  const styles = useStyles();
  const [section, setSection] = useState<FilterSection | null>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<FilterSection | null>(null);
  const rootRefs = useRef(new Map<FilterSection, HTMLDivElement>());
  const kindChanged = !sameActivityKinds(kinds, ALL_ACTIVITY_KINDS);
  const axes = (kindChanged ? 1 : 0) + (actors.length > 0 ? 1 : 0) + (delayedOnly ? 1 : 0);
  const onlyKind = kinds.length === 1 ? kinds[0] : undefined;
  const kindInfo = kindChanged && onlyKind ? ACTIVITY_KIND_LABEL[onlyKind] : undefined;
  const actorInfo = actors.length === 1 ? actors[0] : undefined;
  const singles = [kindInfo, actorInfo, delayedOnly ? "지연" : undefined].filter((value): value is string => !!value);
  const info = axes === 1 && singles.length === 1 ? singles[0] : undefined;
  const sections: FilterSection[] = ["kind", "actor"];
  const hints: Partial<Record<FilterSection, string>> = {
    kind: kindChanged
      ? selectionHint(ACTIVITY_KIND_ORDER.filter((kind) => kinds.includes(kind)).map((kind) => ACTIVITY_KIND_LABEL[kind]))
      : undefined,
    actor: selectionHint(actors),
  };

  const goBack = () => {
    returnFocus.current = section;
    setSection(null);
  };
  useEffect(() => {
    if (section) {
      backRef.current?.focus();
      return;
    }
    const from = returnFocus.current;
    if (!from) return;
    returnFocus.current = null;
    rootRefs.current.get(from)?.focus();
  }, [section]);

  const onChecked = (_: unknown, data: MenuCheckedValueChangeData) => {
    if (data.name === "kind")
      onKinds(data.checkedItems.filter((item): item is ActivityKind => (ACTIVITY_KIND_ORDER as readonly string[]).includes(item)));
    if (data.name === "actor") onActors(data.checkedItems.map(String));
    if (data.name === "delayed") onDelayed(data.checkedItems.includes("only"));
  };

  return (
    <Menu
      checkedValues={{ kind: kinds, actor: actors, delayed: delayedOnly ? ["only"] : [] }}
      onCheckedValueChange={onChecked}
      onOpenChange={(_, data) => {
        if (!data.open) setSection(null);
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <FuiButton className={styles.nowrap} icon={<Filter16Regular />} appearance="subtle" aria-label={axes > 0 ? `필터: ${info ?? axes}` : "필터"}>
          {info ? <FilterLabel info={info} /> : <FilterLabel count={axes > 0 ? axes : undefined} />}
        </FuiButton>
      </MenuTrigger>
      <MenuPopover
        onKeyDown={(event) => {
          if (event.key !== "ArrowLeft" || !section) return;
          event.preventDefault();
          event.stopPropagation();
          goBack();
        }}
      >
        <MenuList>
          {section == null ? (
            <>
              {sections.map((id) => {
                const hint = hints[id];
                return (
                  <MenuItem
                    key={id}
                    persistOnClick
                    ref={(node) => {
                      if (node) rootRefs.current.set(id, node);
                      else rootRefs.current.delete(id);
                    }}
                    aria-label={hint ? `${SECTION_LABEL[id]}, ${hint}` : SECTION_LABEL[id]}
                    secondaryContent={hint ? <span className={styles.hint}>{hint}</span> : undefined}
                    submenuIndicator={<ChevronRight16Regular />}
                    icon={id === "actor" ? <Person16Regular /> : <Filter16Regular />}
                    onClick={() => setSection(id)}
                    onKeyDown={(event) => {
                      if (event.key !== "ArrowRight") return;
                      event.preventDefault();
                      event.stopPropagation();
                      setSection(id);
                    }}
                  >
                    {SECTION_LABEL[id]}
                  </MenuItem>
                );
              })}
              <MenuDivider />
              <MenuItemCheckbox name="delayed" value="only" persistOnClick>
                지연만
              </MenuItemCheckbox>
              {axes > 0 ? (
                <>
                  <MenuDivider />
                  <MenuItem icon={<Dismiss16Regular />} onClick={onReset}>
                    필터 초기화
                  </MenuItem>
                </>
              ) : null}
            </>
          ) : (
            <>
              <MenuItem ref={backRef} persistOnClick icon={<ChevronLeft16Regular />} aria-label={menuBackLabel(SECTION_LABEL[section])} onClick={goBack}>
                {SECTION_LABEL[section]}
              </MenuItem>
              <MenuDivider />
              {section === "kind" &&
                ACTIVITY_KIND_ORDER.map((kind) => (
                  <MenuItemCheckbox key={kind} name="kind" value={kind} icon={kindIcon(kind)}>
                    {ACTIVITY_KIND_LABEL[kind]}
                  </MenuItemCheckbox>
                ))}
              {section === "actor" &&
                (actorOptions.length > 0 ? (
                  actorOptions.map((actor) => (
                    <MenuItemCheckbox key={actor} name="actor" value={actor} icon={<Person16Regular />}>
                      {actor}
                    </MenuItemCheckbox>
                  ))
                ) : (
                  <Caption1 className={styles.hint}>해당하는 이름이 없습니다</Caption1>
                ))}
            </>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function ActivityMockMenu({
  styles,
  scenario,
  onPick,
}: {
  styles: ReturnType<typeof useStyles>;
  scenario: ActivityMockCase;
  onPick: (id: ActivityMockCase) => void;
}) {
  return (
    <Menu
      checkedValues={{ case: scenario === "live" ? [] : [scenario] }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (next === "many" || next === "today" || next === "recent" || next === "empty") onPick(next);
      }}
      positioning="below-start"
    >
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className={styles.mockBtn} aria-label="목업 사례" title="목업 사례">
          <MoreHorizontal16Regular />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {ACTIVITY_MOCK_CASES.map((item) => (
            <MenuItemRadio key={item.id} name="case" value={item.id}>
              {item.label}
            </MenuItemRadio>
          ))}
          <MenuDivider />
          <MenuItem onClick={() => onPick("live")}>초기화</MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

export function ActivityPage({ withDrawers = false }: { withDrawers?: boolean }) {
  const styles = useStyles();
  const { id: planId } = useParams();
  const scopeKey = planId ?? "*";
  const role = useWorkspaceRole();
  const visibilityTick = usePlanVisibilityTick();
  const catalog = useCatalogStore((s) => s.projects);
  const activeId = useProjectStore((s) => s.activeId);
  const live = useProjectStore((s) => s.project);
  const [now] = useState(() => new Date());
  const scenario = useActivityMockCase();
  const [pages, setPages] = useState(1);
  const [dayShown, setDayShown] = useState<Record<string, number>>({});
  const [kinds, setKinds] = useState<ActivityKind[]>([...ALL_ACTIVITY_KINDS]);
  const [actors, setActors] = useState<string[]>([]);
  const [planIds, setPlanIds] = useState<string[]>([]);
  const [delayedOnly, setDelayedOnly] = useState(false);
  const [seenScope, setSeenScope] = useState(scopeKey);

  if (seenScope !== scopeKey) {
    setSeenScope(scopeKey);
    setPages(1);
    setDayShown({});
    setKinds([...ALL_ACTIVITY_KINDS]);
    setActors([]);
    setPlanIds([]);
    setDelayedOnly(false);
  }

  const projects = useMemo(() => {
    void visibilityTick;
    const ids = planId ? [planId] : Object.keys(catalog);
    const list: Project[] = [];
    for (const id of ids) {
      if (!showInPlanList(planVisibilityOf(id), role).shown) continue;
      const project = activeId === id && live.id === id ? live : catalog[id];
      if (!project || project.lifecycle === "trashed") continue;
      list.push(project);
    }
    return list;
  }, [visibilityTick, planId, catalog, role, activeId, live]);

  const events = useMemo(() => {
    if (scenario === "live") return collectActivity(projects);
    return activityMockEvents(scenario, {
      now,
      focusPlanId: planId,
      plans: projects.map((project) => ({ id: project.id, name: project.name })),
    });
  }, [scenario, projects, planId, now]);
  const scopedEvents = useMemo(() => {
    if (planIds.length === 0) return events;
    const set = new Set(planIds);
    return events.filter((event) => set.has(event.planId));
  }, [events, planIds]);

  const filtered = useMemo(
    () => filterActivity(events, { kinds, actors, planIds, query: "", delayedOnly }),
    [events, kinds, actors, planIds, delayedOnly],
  );
  const { shown, hasOlder } = sliceActivity(filtered, now, pages);
  const groups = useMemo(() => groupActivity(shown, now), [shown, now]);
  const description = activityScale(scopedEvents, now);
  const planChoices = useMemo(() => {
    if (planId) return [];
    if (scenario === "live") return projects.length > 1 ? projects.map((project) => ({ id: project.id, name: project.name })) : [];
    const map = new Map<string, string>();
    for (const event of events) map.set(event.planId, event.planName);
    const list = [...map.entries()].map(([id, name]) => ({ id, name }));
    return list.length > 1 ? list.sort((a, b) => a.name.localeCompare(b.name, "ko")) : [];
  }, [planId, scenario, projects, events]);
  const showPlan = planChoices.length > 1;
  useEffect(() => {
    if (planIds.length === 0) return;
    const selected = planIds.length === 1 ? planIds[0] : "";
    if (!selected || !planChoices.some((plan) => plan.id === selected)) setPlanIds([]);
  }, [planIds, planChoices]);

  const pickMock = (next: ActivityMockCase) => {
    setActivityMockCase(next);
    setPages(1);
    setDayShown({});
    setKinds([...ALL_ACTIVITY_KINDS]);
    setActors([]);
    setPlanIds([]);
    setDelayedOnly(false);
  };

  const narrowed = !sameActivityKinds(kinds, ALL_ACTIVITY_KINDS) || actors.length > 0 || planIds.length > 0 || delayedOnly;
  const emptyTitle = scopedEvents.length === 0 ? "활동이 없습니다" : narrowed ? "조건에 맞는 활동이 없습니다" : "최근 7일 활동이 없습니다";
  const emptyDescription =
    scopedEvents.length === 0
      ? "작업, 이슈, 주간 보고가 등록되면 이곳에 남습니다."
      : narrowed
        ? "필터를 바꾸거나 초기화해 보세요."
        : "이전 활동을 더 보면 그 앞의 기록이 나옵니다.";

  const handleResetFilters = () => {
    setKinds([...ALL_ACTIVITY_KINDS]);
    setActors([]);
    setDelayedOnly(false);
    setDayShown({});
  };

  const handleLoadOlder = () => {
    if (filtered.length > 0) {
      const currentStart = activityWindowStart(now, pages);
      const nextOlder = filtered.find((event) => timeOf(event.at) < currentStart);
      if (nextOlder) {
        const diffMs = now.getTime() - timeOf(nextOlder.at);
        const neededPages = Math.max(pages + 1, Math.ceil(diffMs / (ACTIVITY_PAGE_DAYS * 86400000)));
        setPages(neededPages);
        return;
      }
    }
    setPages((count) => count + 1);
  };

  return (
    <div className={styles.page}>
      <ListToolbar
        title="활동"
        description={description}
        titleAside={<ActivityMockMenu styles={styles} scenario={scenario} onPick={pickMock} />}
        filters={
          <>
            {showPlan ? (
              <FuiFilterSelect
                className={styles.planFilter}
                label="플랜"
                ariaLabel="플랜"
                value={planChoices.some((plan) => plan.id === planIds[0]) ? planIds[0]! : "all"}
                options={[{ value: "all", label: "모든 플랜" }, ...planChoices.map((plan) => ({ value: plan.id, label: plan.name }))]}
                onValueChange={(value) => setPlanIds(value === "all" ? [] : [value])}
              />
            ) : null}
            <ActivityFilter
              kinds={kinds}
              actors={actors}
              actorOptions={activityActors(events)}
              delayedOnly={delayedOnly}
              onKinds={setKinds}
              onActors={setActors}
              onDelayed={setDelayedOnly}
              onReset={handleResetFilters}
            />
          </>
        }
      />

      <div className={styles.body}>
        {shown.length === 0 ? (
          <div className={styles.empty}>
            <GhostEmptyState title={emptyTitle} description={emptyDescription} />
            {hasOlder ? (
              <FuiButton appearance="subtle" icon={<History16Regular />} onClick={handleLoadOlder}>
                이전 활동 더 보기
              </FuiButton>
            ) : null}
          </div>
        ) : (
          <>
            {groups.map((group) => (
              <section key={group.key} className={styles.group} aria-label={group.heading}>
                <div className={styles.dayHead}>
                  <span className={styles.dayTitle}>{group.heading}</span>
                </div>
                {group.events.slice(0, dayShown[group.key] ?? DAY_PAGE).map((event) => (
                  <ActivityRow
                    key={event.id}
                    event={event}
                    showPlan={showPlan}
                    onOpen={(event) => {
                      if (event.id.startsWith("mock:")) return;
                      openActivity(event);
                    }}
                    onFilterActor={(actor) => setActors([actor])}
                    onFilterPlan={(id) => setPlanIds([id])}
                  />
                ))}
                {group.events.length > (dayShown[group.key] ?? DAY_PAGE) ? (
                  <div className={styles.dayMore}>
                    <FuiButton
                      appearance="subtle"
                      onClick={() => setDayShown((current) => ({ ...current, [group.key]: (current[group.key] ?? DAY_PAGE) + DAY_PAGE }))}
                    >
                      더 보기
                    </FuiButton>
                  </div>
                ) : null}
              </section>
            ))}
            {hasOlder ? (
              <div className={styles.more}>
                <FuiButton appearance="subtle" icon={<History16Regular />} onClick={handleLoadOlder}>
                  이전 활동 더 보기
                </FuiButton>
              </div>
            ) : null}
          </>
        )}
      </div>

      {withDrawers ? (
        <>
          <ExecutionTaskDrawer />
          <RecordDrawer />
        </>
      ) : null}
    </div>
  );
}
